import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import ServiceStatusMonitor from '../components/ServiceStatusMonitor';
import { optimizedApiService } from '../lib/apiOptimized';
import { errorHandler } from '../lib/errorHandler';
import { Search, Package, MapPin, Clock, CheckCircle, Truck, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface TrackingEvent {
  status: string;
  location: string;
  date: string;
  time: string;
  timestamp: string;
}

interface TrackingInfo {
  status: string;
  events: TrackingEvent[];
  delivery_date?: string;
  service_type?: string;
  has_more_events?: boolean;
  error?: string;
}

interface ShipmentTracking {
  id: number;
  shipment_tracking_number: string | null;
  reference_number: string;
  service_type: string;
  service_type_display: string;
  status: string;
  shipping_cost: number | null;
  currency: string;
  created_at: string;
  updated_at: string;
  to_address: {
    name: string;
    company: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    state_province: string;
    postal_code: string;
    country_code: string;
  };
  from_address: {
    name: string;
    company: string;
    address_line_1: string;
    city: string;
    state_province: string;
    postal_code: string;
    country_code: string;
  };
  package_count: number;
  labels: Array<{
    tracking_number: string;
    package_index: number;
    status: string;
  }>;
  tracking: TrackingInfo | null;
  description: string;
}

interface TrackingPageResponse {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  results: ShipmentTracking[];
}

const Tracking: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedShipments, setExpandedShipments] = useState<Set<number>>(new Set());

  // Fetch all shipments WITHOUT tracking info first (fast, like Karrio)
  const { data: trackingData, isLoading, error, refetch } = useQuery<TrackingPageResponse>({
    queryKey: ['tracking-page', searchQuery, statusFilter],
    queryFn: () => optimizedApiService.shipments.getTrackingPage({
      search: searchQuery || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      page_size: 50,
      fetch_tracking: false  // Don't fetch tracking by default - lazy load when expanded
    }) as Promise<TrackingPageResponse>,
    refetchInterval: 60000, // Refresh every minute
  });

  // Track which shipments need tracking details
  const [trackingDetails, setTrackingDetails] = useState<{[key: number]: any}>({});
  const [loadingTracking, setLoadingTracking] = useState<Set<number>>(new Set());

  // Fetch tracking details when shipment is expanded
  const fetchTrackingDetails = async (shipmentId: number, trackingNumber: string) => {
    if (trackingDetails[shipmentId] || loadingTracking.has(shipmentId)) {
      return; // Already loaded or loading
    }

    setLoadingTracking(prev => new Set(prev).add(shipmentId));
    
    try {
      const response = await optimizedApiService.shipments.getTrackingDetails(shipmentId) as any;
      setTrackingDetails(prev => ({
        ...prev,
        [shipmentId]: response.data || response
      }));
    } catch (err) {
      console.error('Failed to fetch tracking details:', err);
      setTrackingDetails(prev => ({
        ...prev,
        [shipmentId]: { error: 'Failed to load tracking details' }
      }));
    } finally {
      setLoadingTracking(prev => {
        const newSet = new Set(prev);
        newSet.delete(shipmentId);
        return newSet;
      });
    }
  };

  // Handle errors
  React.useEffect(() => {
    if (error) {
      errorHandler.handleApiError(error as any, 'Tracking Page');
    }
  }, [error]);

  const toggleExpanded = (shipmentId: number, trackingNumber: string | null) => {
    const newExpanded = new Set(expandedShipments);
    if (newExpanded.has(shipmentId)) {
      newExpanded.delete(shipmentId);
    } else {
      newExpanded.add(shipmentId);
      // Fetch tracking details when expanding (lazy loading)
      if (trackingNumber) {
        fetchTrackingDetails(shipmentId, trackingNumber);
      }
    }
    setExpandedShipments(newExpanded);
  };

  const getStatusIcon = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('delivered')) {
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    } else if (statusLower.includes('transit') || statusLower.includes('in_transit')) {
      return <Truck className="h-5 w-5 text-blue-600" />;
    } else if (statusLower.includes('out_for_delivery') || statusLower.includes('out for delivery')) {
      return <MapPin className="h-5 w-5 text-yellow-600" />;
    } else if (statusLower.includes('exception') || statusLower.includes('error')) {
      return <AlertCircle className="h-5 w-5 text-red-600" />;
    }
    return <Clock className="h-5 w-5 text-gray-600" />;
  };

  const getStatusBadgeVariant = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('delivered')) return 'success';
    if (statusLower.includes('transit') || statusLower.includes('in_transit')) return 'secondary';
    if (statusLower.includes('out_for_delivery') || statusLower.includes('out for delivery')) return 'warning';
    if (statusLower.includes('exception') || statusLower.includes('error')) return 'error';
    return 'default';
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Service Status Monitor */}
      <ServiceStatusMonitor />

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Track Shipments</h1>
        <p className="text-gray-600">
          View all your shipments and their tracking information in one place.
        </p>
      </div>

      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by tracking number, reference, name, or city..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="md:w-48">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status Filter
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="created">Created</option>
                  <option value="shipped">Shipped / In Transit</option>
                  <option value="in_transit">In Transit</option>
                  <option value="out_for_delivery">Out for Delivery</option>
                  <option value="delivered">Delivered</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button type="submit" variant="primary" className="w-full md:w-auto">
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Shipments List */}
      {isLoading ? (
        <Card>
          <CardContent className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading shipments...</p>
          </CardContent>
        </Card>
      ) : trackingData && trackingData.results.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Showing {trackingData.results.length} of {trackingData.count} shipments
            </p>
          </div>

          {trackingData.results.map((shipment) => (
            <Card key={shipment.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {/* Shipment Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Package className="h-5 w-5 text-gray-400" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {shipment.shipment_tracking_number || `Shipment #${shipment.id}`}
                          </h3>
                          {shipment.reference_number && (
                            <p className="text-sm text-gray-500">Ref: {shipment.reference_number}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 mt-2">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(shipment.tracking?.status || shipment.status)}
                          <Badge variant={getStatusBadgeVariant(shipment.tracking?.status || shipment.status)}>
                            {shipment.tracking?.status || shipment.status}
                          </Badge>
                        </div>
                        <span className="text-sm text-gray-600">{shipment.service_type_display}</span>
                        <span className="text-sm text-gray-600">{shipment.package_count} package(s)</span>
                        {shipment.shipping_cost && (
                          <span className="text-sm font-medium text-gray-900">
                            ${shipment.shipping_cost.toFixed(2)} {shipment.currency}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => toggleExpanded(shipment.id, shipment.shipment_tracking_number)}
                    >
                      {expandedShipments.has(shipment.id) ? 'Hide' : 'View'} Details
                    </Button>
                  </div>

                  {/* Recipient Info */}
                  <div className="border-t pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">To</p>
                        <p className="font-medium">{shipment.to_address.name}</p>
                        <p className="text-sm text-gray-600">
                          {shipment.to_address.city}, {shipment.to_address.state_province} {shipment.to_address.postal_code}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase mb-1">Created</p>
                        <p className="text-sm text-gray-600">
                          {new Date(shipment.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedShipments.has(shipment.id) && (
                    <div className="border-t pt-4 space-y-4">
                      {/* Tracking Events - Load from lazy-loaded tracking details */}
                      {loadingTracking.has(shipment.id) && (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="text-sm text-gray-600 mt-2">Loading tracking details...</p>
                        </div>
                      )}
                      {trackingDetails[shipment.id] && trackingDetails[shipment.id].events && trackingDetails[shipment.id].events.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <Clock className="h-4 w-4 mr-2" />
                            Tracking Events
                          </h4>
                          <div className="space-y-3">
                            {trackingDetails[shipment.id].events.map((event: any, idx: number) => (
                              <div key={idx} className="flex items-start space-x-3 pl-4 border-l-2 border-blue-200">
                                <div className="flex-shrink-0 mt-1">
                                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                </div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">{event.status}</p>
                                  {event.location && (
                                    <p className="text-sm text-gray-600">{event.location}</p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(event.timestamp).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Label Tracking Numbers */}
                      {shipment.labels && shipment.labels.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Package Tracking Numbers</h4>
                          <div className="space-y-2">
                            {shipment.labels.map((label, idx) => (
                              <div key={idx} className="flex items-center space-x-2 text-sm">
                                <span className="text-gray-600">Package {label.package_index}:</span>
                                <code className="px-2 py-1 bg-gray-100 rounded text-gray-900">
                                  {label.tracking_number}
                                </code>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Addresses */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">From Address</h4>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p className="font-medium">{shipment.from_address.name}</p>
                            {shipment.from_address.company && <p>{shipment.from_address.company}</p>}
                            <p>{shipment.from_address.address_line_1}</p>
                            <p>
                              {shipment.from_address.city}, {shipment.from_address.state_province} {shipment.from_address.postal_code}
                            </p>
                            <p>{shipment.from_address.country_code}</p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">To Address</h4>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p className="font-medium">{shipment.to_address.name}</p>
                            {shipment.to_address.company && <p>{shipment.to_address.company}</p>}
                            <p>{shipment.to_address.address_line_1}</p>
                            {shipment.to_address.address_line_2 && <p>{shipment.to_address.address_line_2}</p>}
                            <p>
                              {shipment.to_address.city}, {shipment.to_address.state_province} {shipment.to_address.postal_code}
                            </p>
                            <p>{shipment.to_address.country_code}</p>
                          </div>
                        </div>
                      </div>

                      {/* Delivery Date */}
                      {trackingDetails[shipment.id]?.delivery_date && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-blue-600" />
                            <p className="font-medium text-blue-900">
                              {trackingDetails[shipment.id].status?.toLowerCase().includes('delivered')
                                ? 'Delivered on'
                                : 'Expected Delivery:'}{' '}
                              {new Date(trackingDetails[shipment.id].delivery_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">No shipments found</p>
            <p className="text-sm text-gray-500 mt-1">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Create a shipment to see it here'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Tracking;
